import styled from '@emotion/styled';
import { useTranslation, useHelpHotjarUrl } from '@hotjar/translations';
import { Stack, Text, Button, tokens, Icon } from '@hotjar/hotsauce';
import {
  USER_GOALS,
  useUserPersonalizationInfoQuery,
} from '@hotjar/webapp/domains/activation/user-personalization-info';
import highlightImage from './assets/faq_highlight.jpg';

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const FaqItem = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: ${tokens.spacer.x1pt5} ${tokens.spacer.x2};
  border: 1px solid ${tokens.color.ui_border};
  border-radius: ${tokens.spacer.x1};

  &:hover {
    background-color: ${tokens.color.bg_hover};
  }
`;

const StyledButton = styled(Button)`
  height: ${tokens.spacer.x2pt5};

  &:hover {
    background-color: ${tokens.color.ui_background};
    text-decoration: underline;
  }
`;

const FaqItemIcon = styled(Icon)`
  position: absolute;
  display: flex;
  top: ${tokens.spacer.x2pt5};
  right: ${tokens.spacer.x2};
`;

const FaqItemLink = styled.a`
  display: block;

  &:hover {
    text-decoration: none;
  }
`;

const Highlight = styled.div`
  display: flex;
  border: 1px solid ${tokens.color.ui_border};
  border-radius: ${tokens.spacer.x1};
  overflow: hidden;

  &:hover {
    background-color: ${tokens.color.bg_hover};
  }
`;

const HighlightContent = styled.div`
  padding: ${tokens.spacer.x2};
`;

const HighlightImage = styled.div<{ background: string }>`
  flex: 0 0 35%;
  background: url('${({ background }) => background}');
  background-color: ${tokens.BRAND_COLORS.noon_100};
  background-size: contain;
  background-position: bottom center;
  background-repeat: no-repeat;
  border-left: 1px solid ${tokens.color.ui_border};
`;

export const FaqsCard = (): JSX.Element | null => {
  const { data: userPersonalizationInfo, isFetched } = useUserPersonalizationInfoQuery();
  const { t } = useTranslation();
  const helpHotjarUrl = useHelpHotjarUrl();

  if (!isFetched) {
    return null;
  }

  return (
    <Stack direction="column" spacing="x2">
      <TitleWrapper>
        <Text as="h3" textType="header5">
          {t('activation:space-overview.faqs-section.title')}
        </Text>
        <StyledButton
          icon="open_new_window"
          href={
            userPersonalizationInfo?.goal === USER_GOALS.HEATMAPS
              ? helpHotjarUrl
              : `${helpHotjarUrl}/sections/115003361727-Installation-FAQs`
          }
          as="a"
          isExternal
          kind="ghost"
        >
          {t('activation:space-overview.faqs-section.view-all')}
        </StyledButton>
      </TitleWrapper>
      <FaqItemLink
        data-testid="highlight-link"
        target="_blank"
        href="https://learning.hotjar.com/courses/how-to-install-hotjar/view/"
      >
        <Highlight>
          <HighlightContent>
            <Text
              textType="caption"
              textColor="text_light"
              css={{ marginBottom: tokens.spacer.x0, display: 'block' }}
            >
              {t('activation:space-overview.faqs-section.item.label-course')}
            </Text>
            <Text textType="subtitle1" css={{ marginBottom: tokens.spacer.x1, display: 'block' }}>
              {t('activation:space-overview.faqs-section.item.highlight.title')}
            </Text>
            <Text textType="caption" textColor="text_light" css={{ display: 'block' }}>
              {t('activation:space-overview.faqs-section.item.highlight.description')}
            </Text>
          </HighlightContent>
          <HighlightImage background={highlightImage} />
        </Highlight>
      </FaqItemLink>

      <FaqItemLink
        data-testid="link-with-goal-dependency"
        href={
          userPersonalizationInfo?.goal === USER_GOALS.HEATMAPS
            ? `${helpHotjarUrl}/articles/7053232973975-Heatmap-Data-Troubleshooting`
            : `${helpHotjarUrl}/articles/360016303294`
        }
        target="_blank"
      >
        <FaqItem>
          <FaqItemIcon color="text_default" icon="open_new_window" size="m" />
          <Text textType="caption" textColor="text_light">
            {t('activation:space-overview.faqs-section.item.label-article')}
          </Text>
          <Text css={{ marginBottom: 0 }} as="p" textType="subtitle1">
            {userPersonalizationInfo?.goal === USER_GOALS.HEATMAPS
              ? t('activation:space-overview.faqs-section.item.heatmaps.title')
              : t('activation:space-overview.faqs-section.item.installation.title')}
          </Text>
        </FaqItem>
      </FaqItemLink>
      <FaqItemLink
        data-testid="speed-link"
        href={`${helpHotjarUrl}/articles/115009335727`}
        target="_blank"
      >
        <FaqItem>
          <FaqItemIcon color="text_default" icon="open_new_window" size="m" />
          <Text textType="caption" textColor="text_light">
            {t('activation:space-overview.faqs-section.item.label-article')}
          </Text>
          <Text css={{ marginBottom: 0 }} as="p" textType="subtitle1">
            {t('activation:space-overview.faqs-section.item.speed.title')}
          </Text>
        </FaqItem>
      </FaqItemLink>
    </Stack>
  );
};
